import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  ExternalLink,
  Bug,
  Calendar,
  CheckCircle,
} from 'lucide-react';
import api from '../../lib/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface SecurityFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  endpoint?: string;
  remediation?: string;
  cve?: string;
  cvss?: number;
  evidence?: string;
}

interface SecurityScanSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
}

interface SecurityScanResult {
  id: string;
  scanType: 'nuclei' | 'integration_test';
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt: string | null;
  summary: SecurityScanSummary;
  findings: SecurityFinding[];
  errorMessage?: string;
}

const severityConfig: Record<string, { color: string; bg: string; label: string; icon: React.ReactNode }> = {
  critical: {
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    label: 'Kritisch',
    icon: <AlertCircle size={18} className="text-red-600" />,
  },
  high: {
    color: 'text-orange-700',
    bg: 'bg-orange-50 border-orange-200',
    label: 'Hoch',
    icon: <AlertTriangle size={18} className="text-orange-600" />,
  },
  medium: {
    color: 'text-yellow-700',
    bg: 'bg-yellow-50 border-yellow-200',
    label: 'Mittel',
    icon: <AlertTriangle size={18} className="text-yellow-600" />,
  },
  low: {
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    label: 'Niedrig',
    icon: <Info size={18} className="text-blue-600" />,
  },
  info: {
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200',
    label: 'Info',
    icon: <Info size={18} className="text-gray-500" />,
  },
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return dateStr;
  }
}

function SeverityBadge({ severity }: { severity: string }) {
  const cfg = severityConfig[severity] || severityConfig.info;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function SummaryCount({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div className={`flex flex-col items-center p-3 rounded-lg border ${color === 'red' ? 'bg-red-50 border-red-200' : color === 'orange' ? 'bg-orange-50 border-orange-200' : color === 'yellow' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
      <span className={`text-2xl font-bold ${color === 'red' ? 'text-red-600' : color === 'orange' ? 'text-orange-600' : color === 'yellow' ? 'text-yellow-600' : 'text-gray-600'}`}>
        {count}
      </span>
      <span className="text-xs text-gray-500 mt-1">{label}</span>
    </div>
  );
}

function FindingRow({ finding }: { finding: SecurityFinding }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = severityConfig[finding.severity] || severityConfig.info;

  return (
    <div className={`border rounded-lg ${cfg.bg}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-4 text-left"
      >
        <div className="mt-0.5">
          {expanded ? <ChevronDown size={16} className="shrink-0 text-gray-400" /> : <ChevronRight size={16} className="shrink-0 text-gray-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <SeverityBadge severity={finding.severity} />
            {finding.cve && (
              <span className="text-xs font-mono bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                {finding.cve}
              </span>
            )}
          </div>
          <h4 className="font-medium text-sm text-gray-900">{finding.title}</h4>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-2">
          {finding.description && (
            <p className="text-sm text-gray-600">{finding.description}</p>
          )}
          {finding.endpoint && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <ExternalLink size={12} />
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">{finding.endpoint}</code>
            </div>
          )}
          {finding.remediation && (
            <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
              <span className="font-medium text-green-700">Empfehlung: </span>
              <span className="text-green-600">{finding.remediation}</span>
            </div>
          )}
          {finding.evidence && (
            <div className="bg-gray-100 rounded p-2">
              <code className="text-xs text-gray-700 break-all">{finding.evidence}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SecurityReportTab() {
  const [expandedScans, setExpandedScans] = useState<Set<string>>(new Set());
  const [activeType, setActiveType] = useState<'all' | 'integration_test' | 'nuclei'>('all');
  const [isScanning, setIsScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  const { data: scans, isLoading, error, refetch, isRefetching } = useQuery<SecurityScanResult[]>({
    queryKey: ['security-scans'],
    queryFn: async () => {
      const res = await api.get<SecurityScanResult[]>('/security-scans');
      return res.data;
    },
    refetchInterval: 30000,
  });


  const triggerScan = async () => {
    setIsScanning(true);
    setScanMessage(null);
    try {
      const res = await api.post('/security-scans/run');
      setScanMessage('Scan gestartet — Ergebnisse in ca. 1-2 Min.');
      // Auto-refetch after 10s to pick up results
      setTimeout(() => refetch(), 10000);
    } catch (err: any) {
      setScanMessage('Fehler beim Starten: ' + (err?.message || 'Unbekannt'));
    } finally {
      setIsScanning(false);
    }
  };

  const filteredScans = (scans ?? []).filter(
    (s) => activeType === 'all' || s.scanType === activeType
  );

  const toggleScan = (id: string) => {
    setExpandedScans((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getOverallScore = () => {
    if (!scans || scans.length === 0) return null;
    // Nur den letzten CVE-Scan + letzten Integrationstest addieren
    const latestNuclei = scans.find(s => s.scanType === 'nuclei');
    const latestIntegration = scans.find(s => s.scanType === 'integration_test');
    const relevant = [latestNuclei, latestIntegration].filter(Boolean);
    const totals = relevant.reduce((acc, s) => ({
      critical: acc.critical + s.summary.critical,
      high: acc.high + s.summary.high,
      medium: acc.medium + s.summary.medium,
      low: acc.low + s.summary.low,
      info: acc.info + s.summary.info,
      total: acc.total + s.summary.total,
    }), { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 });
    // Score: 100 - (critical*15 + high*5 + medium*2 + low*1), min 0
    const score = Math.max(0, 100 - (totals.critical * 15 + totals.high * 5 + totals.medium * 2 + totals.low));
    return { ...totals, score };
  };

  const overall = getOverallScore();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={28} className="text-primary" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Sicherheitsbericht</h2>
            <p className="text-sm text-gray-500">
              Automatisierte Penetration-Tests & Sicherheitsanalyse
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="btn-secondary flex items-center gap-2"
          disabled={isLoading || isRefetching}
        >
          <RefreshCw size={16} className={isLoading || isRefetching ? 'animate-spin' : ''} />
          Aktualisieren
        </button>
        <div className="flex items-center gap-2">
          {scanMessage && (
            <span className="text-xs text-gray-500">{scanMessage}</span>
          )}
          <button
            onClick={triggerScan}
            className="btn-primary flex items-center gap-2"
            disabled={isScanning}
          >
            {isScanning ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Shield size={16} />
            )}
            {isScanning ? 'Scanne...' : 'Scan starten'}
          </button>
        </div>
      </div>

      {isLoading && !scans ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Sicherheitsbericht konnte nicht geladen werden</span>
        </div>
      ) : !scans || scans.length === 0 ? (
        <div className="card py-12 text-center">
          <Shield size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-2">Noch kein Sicherheits-Scan durchgeführt</p>
          <p className="text-xs text-gray-400">
            Der Scan läuft automatisch einmal wöchentlich. Ergebnisse erscheinen hier nach dem ersten Durchlauf.
          </p>
        </div>
      ) : (
        <>
          {/* Overall Score */}
          {overall && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Übersicht</h3>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold ${
                  overall.score >= 90 ? 'bg-green-100 text-green-700' :
                  overall.score >= 70 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  Score: {overall.score}/100
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <SummaryCount count={overall.critical} label="Kritisch" color="red" />
                <SummaryCount count={overall.high} label="Hoch" color="orange" />
                <SummaryCount count={overall.medium} label="Mittel" color="yellow" />
                <SummaryCount count={overall.low + overall.info} label="Niedrig/Info" color="gray" />
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="flex gap-2">
            {['all', 'integration_test', 'nuclei'].map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeType === type
                    ? 'bg-primary text-white'
                    : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {type === 'all' ? 'Alle' : type === 'integration_test' ? 'Integrationstests' : 'Nuclei CVE'}
              </button>
            ))}
          </div>

          {/* Scan History */}
          <div className="space-y-3">
            {filteredScans.map((scan) => {
              const isExpanded = expandedScans.has(scan.id);
              const totalFindings = scan.summary.total || 0;

              return (
                <div key={scan.id} className="card overflow-hidden">
                  {/* Scan header */}
                  <button
                    onClick={() => toggleScan(scan.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {scan.scanType === 'nuclei' ? (
                        <Bug size={20} className="text-gray-500" />
                      ) : (
                        <Shield size={20} className="text-gray-500" />
                      )}
                      <div className="text-left">
                        <span className="font-medium text-gray-900">
                          {scan.scanType === 'nuclei' ? 'Nuclei CVE-Scan' : 'Integrationstests'}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <Calendar size={12} />
                          {formatDate(scan.completedAt)}
                          <span className="text-gray-300">|</span>
                          <span className={`font-medium ${
                            totalFindings > 0 ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {totalFindings > 0
                              ? `${totalFindings} Findings`
                              : 'Keine Findings'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                    </div>
                  </button>

                  {/* Findings */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                      {scan.findings.length === 0 ? (
                        <div className="p-6 text-center">
                          <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
                          <p className="text-sm text-green-600">Keine Sicherheitslücken gefunden</p>
                        </div>
                      ) : (
                        scan.findings.map((finding) => (
                          <div key={finding.id} className="p-2">
                            <FindingRow finding={finding} />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
